import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { createRouteLoader } from "@/utils/route-loader";

import Login from "./pages/Login";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsBridge from "@/components/AnalyticsBridge";
import SplashScreen from "@/components/SplashScreen";
import { AuthProvider } from "@/contexts/auth-provider";
import { AuthGuard } from "@/contexts/auth-guard";
import { GuestGuard } from "@/contexts/guest-guard";
import { OrganizationAccessGuard } from "@/contexts/organization-access-guard";
import { AllEventsGuard } from "@/contexts/all-events-guard";
import { SuperadminGuard } from "@/contexts/superadmin-guard";
import { OrganizationProvider } from "@/contexts/organization-provider";

const protectedPageLoaders = {
  "/new": createRouteLoader(() => import("./pages/Chat")),
  "/my-events": createRouteLoader(() => import("./pages/MyEvents")),
  "/all-events": createRouteLoader(() => import("./pages/AllEvents")),
  "/preferred-venues": createRouteLoader(() => import("./pages/PreferredVenues")),
  "/reporting": createRouteLoader(() => import("./pages/Reporting")),
  "/settings": createRouteLoader(() => import("./pages/Settings")),
  "/internal/admin": createRouteLoader(() => import("./pages/SuperadminDashboard")),
  "/onboarding": createRouteLoader(() => import("./pages/Onboarding")),
};

const Chat = lazy(protectedPageLoaders["/new"]);
const Index = lazy(() => import("./pages/Index"));
const MyEvents = lazy(protectedPageLoaders["/my-events"]);
const AllEvents = lazy(protectedPageLoaders["/all-events"]);
const PreferredVenues = lazy(protectedPageLoaders["/preferred-venues"]);
const Signup = lazy(() => import("./pages/Signup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Verify = lazy(() => import("./pages/Verify"));
const PostAuth = lazy(() => import("./pages/PostAuth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(protectedPageLoaders["/onboarding"]);
const AuthError = lazy(() => import("./pages/AuthError"));
const Reporting = lazy(protectedPageLoaders["/reporting"]);
const Settings = lazy(protectedPageLoaders["/settings"]);
const SuperadminDashboard = lazy(protectedPageLoaders["/internal/admin"]);
const Invite = lazy(() => import("./pages/Invite"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const queryClient = new QueryClient();

function ChatRouteRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/new${search}`} replace />;
}

// Fetch code for the requested page while auth and organization checks run.
// This does not render the page, fetch its data, or bypass either access guard.
function RouteCodePreloader() {
  const { pathname } = useLocation();
  useEffect(() => {
    const route = pathname === '/chat' ? '/new' : pathname;
    const loader = Object.prototype.hasOwnProperty.call(protectedPageLoaders, route)
      ? protectedPageLoaders[route as keyof typeof protectedPageLoaders]
      : undefined;
    if (loader) void loader().catch(() => { /* React.lazy retries when rendered. */ });
  }, [pathname]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HelmetProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <RouteCodePreloader />
          <AuthProvider>
            <OrganizationProvider>
              <ScrollToTop />
              <AnalyticsBridge />
              <Suspense fallback={<SplashScreen /> }>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/new" element={<AuthGuard><OrganizationAccessGuard><Chat /></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/chat" element={<ChatRouteRedirect />} />
                <Route path="/my-events" element={<AuthGuard><OrganizationAccessGuard><MyEvents /></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/all-events" element={<AuthGuard><OrganizationAccessGuard><AllEventsGuard><AllEvents /></AllEventsGuard></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/preferred-venues" element={<AuthGuard><OrganizationAccessGuard><PreferredVenues /></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/signup" element={<GuestGuard><Signup /></GuestGuard>} />
                <Route path="/login" element={<GuestGuard><Login /></GuestGuard>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify" element={<GuestGuard><Verify /></GuestGuard>} />
                <Route path="/auth/error" element={<AuthError />} />
                <Route path="/auth/post-auth" element={<PostAuth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<ResetPassword setup />} />
                <Route path="/onboarding" element={<AuthGuard><Onboarding /></AuthGuard>} />
                <Route path="/reporting" element={<AuthGuard><OrganizationAccessGuard><Reporting /></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/settings" element={<AuthGuard><OrganizationAccessGuard><Settings /></OrganizationAccessGuard></AuthGuard>} />
                <Route path="/invite" element={<Invite />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/internal/admin" element={<SuperadminGuard><SuperadminDashboard /></SuperadminGuard>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </OrganizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
